import React, { useState, useEffect } from 'react';
import CurrentBrandContext from 'contexts/current_brand/context';
import { getCurrentSubDomain } from 'services/sub_domain';
import { getBrandBySlug } from 'requests/brand';
import Loader from 'components/ui/loader';
import ApplicationPageNotFound from 'apps/main/page_not_found';
import { setActiveBrand, deleteActiveBrand } from 'services/active_brand';
import { useShare } from 'contexts/share/use_share';
import { PREVIEW_DEPLOY } from 'constants';

const parseColorToRgb = (color) => {
  if (!color) return null;

  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1]),
      g: parseInt(rgbaMatch[2]),
      b: parseInt(rgbaMatch[3]),
    };
  }

  const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16),
    };
  }

  return null;
};

/**
 * Calcule la luminosité relative d'une couleur selon la formule WCAG
 * @param {Object} rgb - Objet avec r, g, b (0-255)
 * @returns {number} - Luminosité relative entre 0 (noir) et 1 (blanc)
 */
const getRelativeLuminance = (rgb) => {
  if (!rgb) return 0;

  const normalize = (value) => {
    value = value / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };

  const r = normalize(rgb.r);
  const g = normalize(rgb.g);
  const b = normalize(rgb.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Détermine la couleur de texte optimale (noir ou blanc) selon le contraste
 * @param {string} backgroundColor - Couleur de fond (hex, rgb, rgba)
 * @returns {string} - '#000000' ou '#ffffff'
 */
const getContrastTextColor = (backgroundColor) => {
  const rgb = parseColorToRgb(backgroundColor);
  if (!rgb) return '#ffffff'; // Fallback vers blanc si parsing échoue

  const luminance = getRelativeLuminance(rgb);
  // Seuil à 0.5 : si fond clair (luminance > 0.5), texte noir, sinon blanc
  return luminance > 0.5 ? '#4d5969' : '#ffffff';
};

/**
 * Retourne une couleur sûre à utiliser sur fond blanc.
 * Si la couleur brand est trop claire (contraste < minContrast), on force un dark fallback.
 */
const getColorOnWhiteBackground = (color, fallbackDark = '#000000', minContrast = 4.5) => {
  const rgb = parseColorToRgb(color);
  if (!rgb) return fallbackDark;

  const Lcolor = getRelativeLuminance(rgb);
  const Lwhite = 1;
  const contrastRatio = (Lwhite + 0.05) / (Lcolor + 0.05);

  return contrastRatio < minContrast ? fallbackDark : color;
};

const injectBrandColors = (brand) => {
  const root = document.documentElement;

  if (brand?.primary_color) {
    // Injecter la couleur primaire
    root.style.setProperty('--brand-primary-color', brand.primary_color);

    // Convertir en RGB pour les variantes avec opacité
    const primaryRgb = parseColorToRgb(brand.primary_color);
    if (primaryRgb) {
      root.style.setProperty('--brand-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }

    // Calculer et injecter la couleur de texte optimale pour le contraste
    const contrastColor = getContrastTextColor(brand.primary_color);
    root.style.setProperty('--on-brand-primary-color', contrastColor);
    const contrastRgb = parseColorToRgb(contrastColor);
    if (contrastRgb) {
      root.style.setProperty('--on-brand-primary-rgb', `${contrastRgb.r}, ${contrastRgb.g}, ${contrastRgb.b}`);
    }

    // Couleur à utiliser sur fond blanc
    const onWhiteColor = getColorOnWhiteBackground(brand.primary_color, '#000000', 4.5);
    root.style.setProperty('--brand-primary-on-white-color', onWhiteColor);
    const onWhiteRgb = parseColorToRgb(onWhiteColor);
    if (onWhiteRgb) {
      root.style.setProperty('--brand-primary-on-white-rgb', `${onWhiteRgb.r}, ${onWhiteRgb.g}, ${onWhiteRgb.b}`);
    }
  }

  if (brand?.secondary_color) {
    root.style.setProperty('--brand-secondary-color', brand.secondary_color);

    // Convertir en RGB pour les variantes avec opacité
    const secondaryRgb = parseColorToRgb(brand.secondary_color);
    if (secondaryRgb) {
      root.style.setProperty('--brand-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    }

    // Calculer et injecter la couleur de texte optimale pour le contraste
    const contrastColor = getContrastTextColor(brand.secondary_color);
    root.style.setProperty('--on-brand-secondary-color', contrastColor);
    const contrastRgb = parseColorToRgb(contrastColor);
    if (contrastRgb) {
      root.style.setProperty('--on-brand-secondary-rgb', `${contrastRgb.r}, ${contrastRgb.g}, ${contrastRgb.b}`);
    }

    const onWhiteColorSecondary = getColorOnWhiteBackground(brand.secondary_color, '#000000', 4.5);
    root.style.setProperty('--brand-secondary-on-white-color', onWhiteColorSecondary);
    const onWhiteSecondaryRgb = parseColorToRgb(onWhiteColorSecondary);
    if (onWhiteSecondaryRgb) {
      root.style.setProperty('--brand-secondary-on-white-rgb', `${onWhiteSecondaryRgb.r}, ${onWhiteSecondaryRgb.g}, ${onWhiteSecondaryRgb.b}`);
    }
  }
};

const CurrentBrandProvider = ({ children }) => {
  const currentSubdomain = getCurrentSubDomain();
  const [currentBrand, setCurrentBrand] = useState(null);
  const [currentBrandLoading, setCurrentBrandLoading] = useState(true);
  const { inShare, brandSlug } = useShare();

  const isOnBrandSubdomain = currentSubdomain && !['app', 'share', 'embed'].includes(currentSubdomain);
  const isOnAppSubdomain = currentSubdomain && currentSubdomain === 'app';

  const getBrandSlug = () => {
    if (inShare) {
      return brandSlug;
    }
    return isOnBrandSubdomain ? currentSubdomain : brandSlug;
  };

  useEffect(() => {
    if (currentBrand) {
      injectBrandColors(currentBrand);
    }
  }, [currentBrand]);

  const fetchCurrentBrand = () => {
    if (currentBrand && currentBrand.slug === getBrandSlug()) {
      return;
    }
    getBrandBySlug(getBrandSlug())
      .then((response) => {
        setCurrentBrand({
          ...response.data.brand,
        });

        setActiveBrand(response.data.brand.slug);
        setCurrentBrandLoading(false);
      })
      .catch(() => {
        deleteActiveBrand();
        setCurrentBrandLoading(false);
      });
  };

  useEffect(() => {
    if (isOnBrandSubdomain || brandSlug) {
      fetchCurrentBrand();
    } else {
      setCurrentBrandLoading(false);
    }
  }, []);

  if (currentBrandLoading) {
    return <Loader />;
  }

  if (!currentBrand && !isOnAppSubdomain && !PREVIEW_DEPLOY) {
    return <ApplicationPageNotFound />;
  }

  return (
    <CurrentBrandContext.Provider
      value={{
        currentBrand,
        hasCurrentBrand: !!currentBrand,
      }}
    >
      {children}
    </CurrentBrandContext.Provider>
  );
};

export default CurrentBrandProvider;
